import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import * as $3Dmol from '3dmol';
import { chemistryApi } from '../../api/chemistry';
import MolstarViewer from './MolstarViewer';
import LinkEnzymeModal from './LinkEnzymeModal';
import ExportButton from '../export/ExportButton';

interface Biologic {
  id: number;
  file_id: number | null;
  name: string | null;
  sequence: string;
  sequence_type: string | null;
  organism: string | null;
  uniprot_id: string | null;
  molecular_weight: number | null;
  properties: Record<string, any>;
  linked_reactions_count?: number;
  linked_ligands_count?: number;
}

interface BiologicsGridProps {
  fileId: number;
  onSelectionChanged?: (selectedIds: number[]) => void;
  onBiologicClick?: (biologic: Biologic) => void;
}

// Sequence renderer - shows truncated sequence with color coding
const SequenceRenderer: React.FC<ICellRendererParams> = (props) => {
  const sequence = props.value || '';
  const maxLen = 50;
  const truncated = sequence.length > maxLen ? sequence.substring(0, maxLen) + '...' : sequence;

  return (
    <span
      className="font-mono text-xs cursor-pointer hover:text-teal-600"
      title={sequence}
    >
      {truncated}
    </span>
  );
};

// UniProt link renderer
const UniProtRenderer: React.FC<ICellRendererParams> = (props) => {
  const uniprotId = props.value;
  if (!uniprotId) return <span className="text-gray-400">-</span>;

  return (
    <a
      href={`https://www.uniprot.org/uniprotkb/${uniprotId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-teal-600 hover:text-teal-800 hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {uniprotId}
    </a>
  );
};

// Type badge renderer
const TypeRenderer: React.FC<ICellRendererParams> = (props) => {
  const type = props.value || 'unknown';
  const colors: Record<string, string> = {
    protein: 'bg-blue-100 text-blue-800',
    enzyme: 'bg-green-100 text-green-800',
    peptide: 'bg-purple-100 text-purple-800',
    antibody: 'bg-orange-100 text-orange-800',
    dna: 'bg-red-100 text-red-800',
    rna: 'bg-yellow-100 text-yellow-800',
    unknown: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${colors[type] || colors.unknown}`}>
      {type}
    </span>
  );
};

// Molecular weight formatter
const MWFormatter = (params: any) => {
  const mw = params.value;
  if (!mw) return '-';
  return mw.toLocaleString(undefined, { maximumFractionDigits: 1 }) + ' Da';
};

// Length from sequence
const LengthGetter = (params: any) => {
  return params.data?.sequence?.length || 0;
};

// Linked reactions renderer
const LinkedReactionsRenderer: React.FC<ICellRendererParams & { onLinkClick?: (biologic: Biologic) => void }> = (props) => {
  const count = props.value || 0;
  const biologic = props.data as Biologic;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.context?.onLinkClick) {
      props.context.onLinkClick(biologic);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`px-2 py-1 rounded text-xs font-medium ${
        count > 0
          ? 'bg-green-100 text-green-800 hover:bg-green-200'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
      title={count > 0 ? `${count} linked reaction(s) - click to manage` : 'Click to link reactions'}
    >
      {count > 0 ? `${count} linked` : 'Link'}
    </button>
  );
};

// Linked ligands (molecules) renderer
const LinkedLigandsRenderer: React.FC<ICellRendererParams> = (props) => {
  const count = props.value || 0;

  return (
    <span
      className={`px-2 py-1 rounded text-xs font-medium ${
        count > 0
          ? 'bg-purple-100 text-purple-800'
          : 'bg-gray-100 text-gray-500'
      }`}
      title={count > 0 ? `${count} linked molecule(s)` : 'No linked molecules'}
    >
      {count > 0 ? `${count} ligand${count !== 1 ? 's' : ''}` : '-'}
    </span>
  );
};

// Actions renderer with View Details button
const ActionsRenderer: React.FC<ICellRendererParams> = (props) => {
  const biologic = props.data as Biologic;

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.context?.onViewDetails) {
      props.context.onViewDetails(biologic);
    }
  };

  return (
    <button
      onClick={handleViewClick}
      className="px-2 py-1 text-xs font-medium text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded transition-colors"
      title="View protein details"
    >
      View Details
    </button>
  );
};

export const BiologicsGrid: React.FC<BiologicsGridProps> = ({
  fileId,
  onSelectionChanged,
  onBiologicClick,
}) => {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const [rowData, setRowData] = useState<Biologic[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedBiologic, setSelectedBiologic] = useState<Biologic | null>(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkModalBiologic, setLinkModalBiologic] = useState<Biologic | null>(null);
  const [linkedCounts, setLinkedCounts] = useState<Record<number, number>>({});
  const [ligandCounts, setLigandCounts] = useState<Record<number, number>>({});

  const columnDefs: ColDef[] = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 50,
      pinned: 'left',
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 200,
    },
    {
      field: 'sequence_type',
      headerName: 'Type',
      width: 100,
      cellRenderer: TypeRenderer,
    },
    {
      field: 'sequence',
      headerName: 'Sequence',
      flex: 3,
      minWidth: 300,
      cellRenderer: SequenceRenderer,
    },
    {
      headerName: 'Length',
      width: 100,
      valueGetter: LengthGetter,
    },
    {
      field: 'molecular_weight',
      headerName: 'MW',
      width: 120,
      valueFormatter: MWFormatter,
    },
    {
      field: 'uniprot_id',
      headerName: 'UniProt',
      width: 120,
      cellRenderer: UniProtRenderer,
    },
    {
      field: 'organism',
      headerName: 'Organism',
      width: 150,
    },
    {
      field: 'linked_reactions_count',
      headerName: 'Reactions',
      width: 100,
      cellRenderer: LinkedReactionsRenderer,
      valueGetter: (params: any) => linkedCounts[params.data?.id] || 0,
    },
    {
      field: 'linked_ligands_count',
      headerName: 'Ligands',
      width: 100,
      cellRenderer: LinkedLigandsRenderer,
      valueGetter: (params: any) => ligandCounts[params.data?.id] || 0,
    },
    {
      headerName: '',
      width: 110,
      cellRenderer: ActionsRenderer,
      sortable: false,
      filter: false,
      pinned: 'right',
    },
  ];

  const loadLinkedCounts = useCallback(async (biologics: Biologic[]) => {
    const reactionCounts: Record<number, number> = {};
    const moleculeCounts: Record<number, number> = {};

    // Fetch linked counts for each biologic
    await Promise.all(
      biologics.map(async (biologic) => {
        try {
          const [reactionsRes, ligandsRes] = await Promise.all([
            chemistryApi.getBiologicReactions(biologic.id),
            chemistryApi.getBiologicLigands(biologic.id),
          ]);
          reactionCounts[biologic.id] = reactionsRes.total;
          moleculeCounts[biologic.id] = ligandsRes.total;
        } catch {
          reactionCounts[biologic.id] = 0;
          moleculeCounts[biologic.id] = 0;
        }
      })
    );

    setLinkedCounts(reactionCounts);
    setLigandCounts(moleculeCounts);
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await chemistryApi.getBiologics(fileId);
      const biologics = response.biologics || [];
      setRowData(biologics);
      setTotalCount(response.total);
      // Load linked counts in background
      loadLinkedCounts(biologics);
    } catch (error) {
      console.error('Error loading biologics:', error);
    } finally {
      setLoading(false);
    }
  }, [fileId, loadLinkedCounts]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    params.api.sizeColumnsToFit();
  }, []);

  const handleSelectionChanged = useCallback(() => {
    if (gridRef.current?.api && onSelectionChanged) {
      const selectedRows = gridRef.current.api.getSelectedRows();
      onSelectionChanged(selectedRows.map((row: Biologic) => row.id));
    }
  }, [onSelectionChanged]);

  const handleRowDoubleClicked = useCallback(
    (event: any) => {
      if (event.data) {
        // If has PDB content in properties or UniProt ID, open structure viewer
        const biologic = event.data as Biologic;
        if (biologic.properties?.pdb_content || biologic.uniprot_id) {
          setSelectedBiologic(biologic);
          setViewerOpen(true);
        }
        // Also call external handler if provided
        if (onBiologicClick) {
          onBiologicClick(biologic);
        }
      }
    },
    [onBiologicClick]
  );

  const handleCloseViewer = useCallback(() => {
    setViewerOpen(false);
  }, []);

  const handleLinkClick = useCallback((biologic: Biologic) => {
    setLinkModalBiologic(biologic);
    setLinkModalOpen(true);
  }, []);

  const handleViewDetails = useCallback((biologic: Biologic) => {
    navigate(`/protein/${biologic.id}`);
  }, [navigate]);

  const handleCloseLinkModal = useCallback(() => {
    setLinkModalOpen(false);
    setLinkModalBiologic(null);
  }, []);

  const handleLinksUpdated = useCallback(() => {
    // Refresh linked counts
    loadLinkedCounts(rowData);
  }, [loadLinkedCounts, rowData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm text-gray-600">
          {totalCount} sequence{totalCount !== 1 ? 's' : ''}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            Double-click row to view 3D structure
          </span>
          <ExportButton
            dataType="proteins"
            totalCount={totalCount}
            fileId={fileId}
            compact
          />
        </div>
      </div>
      <div className={`ag-theme-alpine ${viewerOpen ? '' : 'flex-1'}`} style={{ minHeight: viewerOpen ? '200px' : '400px', flex: viewerOpen ? '1 1 50%' : undefined }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          rowSelection="multiple"
          onGridReady={onGridReady}
          onSelectionChanged={handleSelectionChanged}
          onRowDoubleClicked={handleRowDoubleClicked}
          suppressRowClickSelection={true}
          animateRows={true}
          context={{ onLinkClick: handleLinkClick, onViewDetails: handleViewDetails }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
        />
      </div>

      {/* Inline 3D Structure Viewer */}
      {viewerOpen && selectedBiologic && (
        <InlineStructurePanel
          biologic={selectedBiologic}
          onClose={handleCloseViewer}
        />
      )}

      {/* Link Enzyme Modal */}
      {linkModalBiologic && (
        <LinkEnzymeModal
          isOpen={linkModalOpen}
          onClose={handleCloseLinkModal}
          biologicId={linkModalBiologic.id}
          biologicName={linkModalBiologic.name}
          onLinksUpdated={handleLinksUpdated}
        />
      )}
    </div>
  );
};

// =====================================================================
// Inline 3D structure panel — constrained height, no modal overlay
// =====================================================================

type ViewStyle = 'cartoon' | 'stick' | 'sphere' | 'surface';
type ColorScheme = 'chainHetatm' | 'spectrum' | 'ssJmol' | 'residue';

const InlineStructurePanel: React.FC<{
  biologic: Biologic;
  onClose: () => void;
}> = ({ biologic, onClose }) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const glviewerRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdbData, setPdbData] = useState<string | null>(null);
  const [source, setSource] = useState('');
  const [viewStyle, setViewStyle] = useState<ViewStyle>('cartoon');
  const [colorScheme, setColorScheme] = useState<ColorScheme>('chainHetatm');
  const [showMolstar, setShowMolstar] = useState(false);

  // Fetch PDB data
  useEffect(() => {
    const pdbContent = biologic.properties?.pdb_content;
    if (pdbContent) {
      setPdbData(pdbContent);
      setSource('uploaded');
      return;
    }

    if (!biologic.uniprot_id) {
      setError('No UniProt ID — structure unavailable');
      return;
    }

    setLoading(true);
    setError(null);
    chemistryApi.fetchAlphaFold(biologic.uniprot_id)
      .then(res => {
        setPdbData(res.pdb_content);
        setSource(`AlphaFold: ${biologic.uniprot_id}`);
      })
      .catch(err => setError(err.message || 'Failed to load structure'))
      .finally(() => setLoading(false));
  }, [biologic]);

  // Render 3Dmol viewer
  useEffect(() => {
    if (!pdbData || !viewerRef.current) return;

    if (!glviewerRef.current) {
      glviewerRef.current = $3Dmol.createViewer(viewerRef.current, {
        backgroundColor: '#f9fafb',
      });
    }

    const viewer = glviewerRef.current;
    viewer.removeAllModels();
    viewer.addModel(pdbData, 'pdb');

    // Apply style
    viewer.setStyle({}, {});
    const colorSpec: any = {};
    switch (colorScheme) {
      case 'spectrum': colorSpec.color = 'spectrum'; break;
      case 'ssJmol':
        colorSpec.colorfunc = (atom: any) => {
          if (atom.ss === 'h') return 0xff0080;
          if (atom.ss === 's') return 0xffc800;
          return 0x808080;
        };
        break;
      case 'residue': colorSpec.colorscheme = 'amino'; break;
      default: colorSpec.colorscheme = 'chainHetatm';
    }
    switch (viewStyle) {
      case 'cartoon': viewer.setStyle({}, { cartoon: { ...colorSpec } }); break;
      case 'stick': viewer.setStyle({}, { stick: { ...colorSpec, radius: 0.2 } }); break;
      case 'sphere': viewer.setStyle({}, { sphere: { ...colorSpec, scale: 0.3 } }); break;
      case 'surface':
        viewer.setStyle({}, { cartoon: { ...colorSpec, opacity: 0.5 } });
        viewer.addSurface($3Dmol.SurfaceType.VDW, { opacity: 0.7, color: 'white' });
        break;
    }
    viewer.zoomTo();
    viewer.render();
  }, [pdbData, viewStyle, colorScheme]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (glviewerRef.current) {
        glviewerRef.current.clear();
        glviewerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="flex flex-col border-t border-gray-200 dark:border-gray-700" style={{ flex: '0 0 350px', minHeight: 250, maxHeight: '60vh' }}>
      {/* Panel header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            {biologic.name || 'Structure'}
          </h4>
          {source && <span className="text-xs text-gray-400">{source}</span>}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={viewStyle}
            onChange={(e) => setViewStyle(e.target.value as ViewStyle)}
            className="px-2 py-1 text-xs border rounded bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
          >
            <option value="cartoon">Cartoon</option>
            <option value="stick">Stick</option>
            <option value="sphere">Sphere</option>
            <option value="surface">Surface</option>
          </select>
          <select
            value={colorScheme}
            onChange={(e) => setColorScheme(e.target.value as ColorScheme)}
            className="px-2 py-1 text-xs border rounded bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
          >
            <option value="chainHetatm">By Chain</option>
            <option value="spectrum">Rainbow</option>
            <option value="ssJmol">Secondary Structure</option>
            <option value="residue">By Residue</option>
          </select>
          {(biologic.uniprot_id || biologic.properties?.pdb_content) && (
            <button
              onClick={() => setShowMolstar(true)}
              className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 font-medium"
              title="Open in Mol* for advanced analysis"
            >
              Mol*
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            title="Close viewer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* 3D viewer */}
      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/75 dark:bg-gray-800/75 z-10">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500" />
              <span className="text-sm text-gray-600 dark:text-gray-300">Loading structure...</span>
            </div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800 z-10">
            <p className="text-sm text-gray-400">{error}</p>
          </div>
        )}
        <div ref={viewerRef} className="w-full h-full" />
      </div>

      {/* Mol* advanced viewer (modal, only when requested) */}
      <MolstarViewer
        isOpen={showMolstar}
        onClose={() => setShowMolstar(false)}
        pdbContent={pdbData || undefined}
        uniprotId={biologic.uniprot_id || undefined}
        title={biologic.name || 'Structure'}
      />
    </div>
  );
};

export default BiologicsGrid;
